/**
 * V49 — Admin Dashboard orientation control.
 *
 * Three modes (per-device, localStorage):
 *   • portrait  — show overlay when the device is in landscape and viewport is mobile/tablet
 *   • landscape — show overlay when the device is in portrait and viewport is mobile/tablet
 *   • auto      — never show the overlay; let the responsive layout handle it
 *
 * Default = "portrait" because the admin dashboard's mobile UI (header,
 * AdminMobileFab, MobileBottomNav, cards) is portrait-first. The overlay
 * never appears on real desktops (>= 1280 px) — the lg/xl layout there is
 * a proper desktop grid that works in either orientation.
 *
 * We deliberately use an overlay (vs. a CSS transform rotate hack) because
 * rotation transforms break:
 *   • touch coordinate hit-testing
 *   • OS-level keyboard/IME positioning
 *   • viewport-relative units (vh/vw)
 *   • position:fixed + safe-area-inset
 * The overlay pattern is what Instagram, banking apps and most production
 * dashboards use — it's also fully accessible.
 */
import { useCallback, useEffect, useState } from "react";

export const ADMIN_ORIENTATION_KEY = "admin_orientation_mode";

export const ORIENTATION_MODES = ["portrait", "landscape", "auto"];

const DEFAULT_MODE = "portrait";

const LANDSCAPE_QUERY = "(orientation: landscape)";
// Below 1280 px we treat the device as mobile/tablet (the admin dashboard
// switches to its desktop grid at lg:1024 internally, but the orientation
// overlay only kicks in for true mobile/tablet form factors).
const MOBILE_MAX_PX = 1024;

function readMode() {
    if (typeof window === "undefined") return DEFAULT_MODE;
    try {
        const v = window.localStorage.getItem(ADMIN_ORIENTATION_KEY);
        return ORIENTATION_MODES.includes(v) ? v : DEFAULT_MODE;
    } catch (e) {
        return DEFAULT_MODE;
    }
}

function writeMode(mode) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(ADMIN_ORIENTATION_KEY, mode);
        // Broadcast so any other open tab / sibling component re-reads.
        window.dispatchEvent(new CustomEvent("admin-orientation-change", { detail: mode }));
    } catch (e) {
        /* storage disabled — silently degrade to auto */
    }
}

export function useAdminOrientation() {
    const [mode, setModeState] = useState(readMode);

    // Listen for cross-tab + intra-page updates
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === ADMIN_ORIENTATION_KEY) setModeState(readMode());
        };
        const onCustom = (e) => {
            if (e?.detail && ORIENTATION_MODES.includes(e.detail)) setModeState(e.detail);
        };
        window.addEventListener("storage", onStorage);
        window.addEventListener("admin-orientation-change", onCustom);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("admin-orientation-change", onCustom);
        };
    }, []);

    const setMode = useCallback((next) => {
        if (!ORIENTATION_MODES.includes(next)) return;
        writeMode(next);
        setModeState(next);
    }, []);

    return { mode, setMode };
}

/**
 * Returns the current physical device state — purely informational so the
 * Guard can decide whether to render the overlay.
 *
 *   { isLandscape, isMobile }
 *
 * Both flags update on resize / orientationchange.
 */
export function useDeviceOrientation() {
    const getState = () => {
        if (typeof window === "undefined") return { isLandscape: false, isMobile: false };
        const isLandscape = window.matchMedia
            ? window.matchMedia(LANDSCAPE_QUERY).matches
            : window.innerWidth > window.innerHeight;
        const isMobile = window.innerWidth < MOBILE_MAX_PX;
        return { isLandscape, isMobile };
    };
    const [state, setState] = useState(getState);

    useEffect(() => {
        const update = () => setState(getState());
        window.addEventListener("resize", update);
        window.addEventListener("orientationchange", update);
        let mql = null;
        if (window.matchMedia) {
            mql = window.matchMedia(LANDSCAPE_QUERY);
            // Older WebKit uses addListener / removeListener.
            if (mql.addEventListener) mql.addEventListener("change", update);
            else if (mql.addListener) mql.addListener(update);
        }
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("orientationchange", update);
            if (mql) {
                if (mql.removeEventListener) mql.removeEventListener("change", update);
                else if (mql.removeListener) mql.removeListener(update);
            }
        };
    }, []);

    return state;
}

export const ORIENTATION_LABELS = {
    portrait: "Portrait Only",
    landscape: "Landscape Only",
    auto: "Auto Rotate",
};

export const ORIENTATION_DESCRIPTIONS = {
    portrait: "Lock the admin dashboard to portrait on phones & tablets (recommended).",
    landscape: "Lock the admin dashboard to landscape on phones & tablets.",
    auto: "Let the dashboard follow the device orientation.",
};
